import React, { useState } from 'react';
import './PrivacyPolicy.css';

interface ContactInfo {
  app: string;
  email: string;
  location: string;
}

interface Section {
  id: string;
  title: string;
  content?: string;
  items?: string[];
  contact?: ContactInfo;
}

const sections: Section[] = [
  {
    id: 'introduction',
    title: 'Introduction',
    content: `BasniConnect ("we," "our," or "us") is committed to protecting your privacy. This policy explains how we handle information when you use our app.`,
  },
  {
    id: 'information-we-collect',
    title: 'Information We Collect',
    content: `We collect information you provide when creating an account or posting content, such as your name, email address, phone number, profile photo, and location. We also collect content you post in the app (listings, announcements, etc.).`,
  },
  {
    id: 'how-we-use',
    title: 'How We Use Your Information',
    items: [
      'To create and manage your account',
      'To display your content to other users',
      'To improve app performance and user experience',
      'To ensure safety and prevent misuse',
    ],
  },
  {
    id: 'sharing',
    title: 'Sharing of Your Information',
    content: `We do not sell your personal data. Content you post (such as listings or announcements) is visible to other app users. We may share data with trusted service providers who help us operate the app, or when required by law.`,
  },
  {
    id: 'security',
    title: 'Data Security',
    content: `We use industry-standard security practices to protect your data. No method of internet transmission is 100% secure, but we take reasonable steps to safeguard your information.`,
  },
  {
    id: 'your-rights',
    title: 'Your Rights',
    content: `You may request access to, correction of, or deletion of your personal data at any time by contacting us.`,
  },
  {
    id: 'children',
    title: "Children's Privacy",
    content: `BasniConnect is not intended for children. We do not knowingly collect personal data from minors. If you believe a minor has submitted their information, please contact us and we will remove it promptly.`,
  },
  {
    id: 'changes',
    title: 'Changes to This Policy',
    content: `We may update this policy occasionally. Continued use of the app after changes means you accept the updated policy.`,
  },
  {
    id: 'contact',
    title: 'Contact Us',
    content: `For any questions about this Privacy Policy, contact us:`,
    contact: {
      app: 'BasniConnect',
      email: 'mohammedrazachandwala@gmail.com',
      location: 'Mumbai, India',
    },
  },
];

const PrivacyPolicy: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string>('introduction');

  const scrollTo = (id: string) => {
    setActiveSection(id);
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="pp-root">
      <header className="pp-header">
        <div className="pp-header-inner">
          <div className="pp-logo">
            <img src="/app_logo.png" alt="BasniConnect" className="pp-logo-img" />
            <span className="pp-logo-text">BasniConnect</span>
          </div>
          <div className="pp-header-meta">
            <span className="pp-badge">Privacy Policy</span>
            <span className="pp-updated">Last updated: May 2, 2026</span>
          </div>
        </div>
      </header>

      <div className="pp-body">
        <nav className="pp-sidebar">
          <p className="pp-sidebar-label">Contents</p>
          <ul className="pp-nav-list">
            {sections.map((s, i) => (
              <li key={s.id}>
                <button
                  className={`pp-nav-item ${activeSection === s.id ? 'active' : ''}`}
                  onClick={() => scrollTo(s.id)}
                >
                  <span className="pp-nav-num">{String(i + 1).padStart(2, '0')}</span>
                  <span className="pp-nav-title">{s.title}</span>
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <main className="pp-content">
          <div className="pp-hero">
            <h1 className="pp-title">Privacy Policy</h1>
            <p className="pp-subtitle">
              Simple and transparent — here's how we handle your information.
            </p>
          </div>

          {sections.map((section, i) => (
            <section key={section.id} id={section.id} className="pp-section">
              <div className="pp-section-header">
                <span className="pp-section-num">{String(i + 1).padStart(2, '0')}</span>
                <h2 className="pp-section-title">{section.title}</h2>
              </div>

              {section.content && (
                <p className="pp-text">{section.content}</p>
              )}

              {section.items && (
                <ul className="pp-list pp-list-top">
                  {section.items.map((item) => (
                    <li key={item} className="pp-list-item">
                      <span className="pp-list-dot" />
                      {item}
                    </li>
                  ))}
                </ul>
              )}

              {section.contact && (
                <div className="pp-contact-card">
                  <div className="pp-contact-row">
                    <span className="pp-contact-label">App</span>
                    <span className="pp-contact-value">{section.contact.app}</span>
                  </div>
                  <div className="pp-contact-row">
                    <span className="pp-contact-label">Email</span>
                    <a href={`mailto:${section.contact.email}`} className="pp-contact-link">
                      {section.contact.email}
                    </a>
                  </div>
                  <div className="pp-contact-row">
                    <span className="pp-contact-label">Location</span>
                    <span className="pp-contact-value">{section.contact.location}</span>
                  </div>
                </div>
              )}
            </section>
          ))}

          <footer className="pp-footer">
            <div className="pp-footer-logo">
              <img src="/app_logo.png" alt="BasniConnect" className="pp-logo-img pp-logo-img--sm" />
              <span className="pp-logo-text">BasniConnect</span>
            </div>
            <p className="pp-footer-text">
              © {new Date().getFullYear()} BasniConnect. All rights reserved.
            </p>
          </footer>
        </main>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
